import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-file-input.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { DefaultContainerLoader } from '../../components/loaders.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { getMatchingWeekCalendarFeedToken } from '../../../api/calendar-feed.js';
import '../../components/bh-topbar-copy.js';
import { CandidateJobPostingOverview } from './candidate-job-posting-overview.js';

const getBuchungsheldRootUrlByDeployment = () => {
  const deployment = baseConfig.get?.('DEPLOYMENT');
  switch (deployment) {
    case 'development':
      return baseConfig.get('ROOT_URL');
    case 'qa':
      return 'https://buchungsheld2.qa.adornis.de/';
    case 'staging':
      return 'https://buchungsheld-tuda.staging.adornis.de/';
    case 'production':
      return 'https://www.icsrm.hrz.tu-darmstadt.de/';
    default:
      // fallback to qa
      return 'https://buchungsheld2.qa.adornis.de/';
  }
};

function getExplanations(lang) {
  if (lang === 'de')
    return [
      {
        title: 'Google Kalendar',
        steps: [
          { text: 'Öffne deinen Google Kalender in einem Webbrowser.' },
          {
            text: 'In der linken Seitenleiste siehst du eine Liste mit Kalendern. Klicke auf das Pluszeichen (+) neben „Weitere Kalender“.',
          },
          { text: 'Wähle die Option „Über URL hinzufügen“ aus.' },
          { text: 'Füge den ICS-Link in das Feld „URL des Kalenders“ ein.' },
          { text: 'Klicke auf „Kalender hinzufügen“ und der Kalender wird zu deinem Google Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Microsoft Outlook',
        steps: [
          { text: 'Öffne Outlook in einem Webbrowser oder der Desktopanwendung.' },
          { text: 'Klicke auf „Kalender“ in der unteren Symbolleiste, um den Kalender zu öffnen.' },
          { text: 'Klicke oben auf „Kalender hinzufügen“ und wähle „Aus dem Internet“ aus.' },
          { text: 'Füge den ICS-Link in das Feld „Link zum Kalender“ ein.' },
          { text: 'Klicke auf „Speichern“ und der Kalender wird zu deinem Outlook-Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Apple Kalender (macOS)',
        steps: [
          { text: 'Öffne den Apple Kalender auf deinem Mac.' },
          { text: 'Klicke im Menü „Ablage“ auf „Neues Kalenderabonnement“.' },
          { text: 'Füge den ICS-Link in das Feld „URL“ ein.' },
          { text: 'Klicke auf „Abonnieren“ und passe gegebenenfalls die Einstellungen an.' },
          { text: 'Klicke auf „OK“ und der Kalender wird zu deinem Apple Kalender hinzugefügt.' },
        ],
      },
      {
        title: 'Apple Kalender (iOS)',
        steps: [
          { text: 'Kopiere den ICS-Link auf deinem iOS-Gerät.' },
          { text: 'Gehe zu „Einstellungen“ und tippe auf „Kalender“.' },
          { text: 'Wähle „Account hinzufügen“ und dann „Andere“.' },
          { text: 'Wähle „Kalender hinzufügen“ und füge den ICS-Link in das Feld „Server“ ein.' },
          { text: 'Tippe auf „Weiter“ und passe gegebenenfalls die Einstellungen an.' },
          { text: 'Tippe auf „Sichern“ und der Kalender wird zu deinem Apple Kalender hinzugefügt.' },
        ],
      },
    ];

  return [
    {
      title: 'Google Calendar',
      steps: [
        { text: 'Open your Google Calendar in a web browser.' },
        {
          text: 'In the left sidebar, you will see a list of calendars. Click the plus sign (+) next to "Other Calendars".',
        },
        { text: 'Select the "Add by URL" option.' },
        { text: 'Paste the ICS link into the "URL of the calendar" field.' },
        { text: 'Click "Add Calendar" and the calendar will be added to your Google Calendar.' },
      ],
    },
    {
      title: 'Microsoft Outlook',
      steps: [
        { text: 'Open Outlook in a web browser or the desktop application.' },
        { text: 'Click "Calendar" in the lower symbol bar to open the calendar.' },
        { text: 'Click "Add Calendar" at the top and select "From the Internet".' },
        { text: 'Paste the ICS link into the "Link to calendar" field.' },
        { text: 'Click "Save" and the calendar will be added to your Outlook calendar.' },
      ],
    },
    {
      title: 'Apple Calendar (macOS)',
      steps: [
        { text: 'Open the Apple Calendar on your Mac.' },
        { text: 'Click the "Archive" menu and select "New Calendar Subscription".' },
        { text: 'Paste the ICS link into the "URL" field.' },
        { text: 'Click "Subscribe" and adjust settings, if necessary.' },
        { text: 'Click "OK" and the calendar will be added to your Apple Calendar.' },
      ],
    },
    {
      title: 'Apple Calendar (iOS)',
      steps: [
        { text: 'Copy the ICS link on your iOS device.' },
        { text: 'Go to "Settings" and tap "Calendar".' },
        { text: 'Select "Add Account" and then "Other".' },
        { text: 'Select "Add Calendar" and paste the ICS link into the "Server" field.' },
        { text: 'Tap "Continue" and adjust settings, if necessary.' },
        { text: 'Tap "Save" and the calendar will be added to your Apple Calendar.' },
      ],
    },
  ];
}

export const CandidateProfile = stateful(() => {
  const user = useQuery(() => currentUser<User>().result, []);
  const calendarFeedToken = useQuery(() => getMatchingWeekCalendarFeedToken()(), []);

  if (user.loading || calendarFeedToken.loading) {
    return DefaultContainerLoader();
  }

  const design = useObservable(() => DesignSystem.currentTheme, []);
  const calendarLink =
    user.value?._id && calendarFeedToken.value
      ? `${window.location.origin}/calendar/${user.value._id}.ics?token=${encodeURIComponent(calendarFeedToken.value)}`
      : '';

  return html`
    <x-flex class="max-h-full overflow-auto">
      <bh-topbar-copy></bh-topbar-copy>
      <x-flex space="lg" ${css({ height: '100%', overflow: 'auto' })}>
        <x-section ${css({ padding: `${design?.spacing.md} 0`, flexGrow: '1' })} space="lg">
          <!-- Mobile Tabs -->
          <div class="block lg:hidden mb-6 border-b border-gray-200">
            <div class="flex">
              <a
                class="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer"
                @click=${() =>
                  window.open(`${getBuchungsheldRootUrlByDeployment()}profile?preselectedTab=events`, '_self')}
              >
                ${m.candidate_profile_nav_events()}
              </a>
              <a
                class="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer"
                @click=${() =>
                  window.open(`${getBuchungsheldRootUrlByDeployment()}profile?preselectedTab=profile`, '_self')}
              >
                ${m.candidate_profile_nav_profile()}
              </a>
              <a class="px-4 py-2 bg-primary text-white cursor-pointer"> ${m.candidate_profile_nav_matching_week()} </a>
            </div>
          </div>

          <!-- Desktop Layout with Sidebar -->
          <div class="hidden lg:block">
            <x-flex horizontal space="lg">
              <x-flex
                vertical
                ${css({
                  minWidth: '220px',
                  borderRight: `1px solid ${design?.colors.neutral.shade(100)}`,
                  padding: `${design?.spacing.lg} 0`,
                })}
              >
                <x-flex
                  padding="sm"
                  ${css({
                    cursor: 'pointer',
                    borderLeft: '4px solid transparent',
                    backgroundColor: 'transparent',
                    paddingLeft: design?.spacing.sm,
                    color: 'inherit',
                    fontWeight: 'normal',
                  })}
                  @click=${() =>
                    window.open(`${getBuchungsheldRootUrlByDeployment()}profile?preselectedTab=events`, '_self')}
                >
                  ${m.candidate_profile_nav_events()}
                </x-flex>

                <x-flex
                  padding="sm"
                  ${css({
                    cursor: 'pointer',
                    borderLeft: `4px solid transparent`,
                    backgroundColor: 'transparent',
                    paddingLeft: design?.spacing.sm,
                    color: 'inherit',
                    fontWeight: 'normal',
                  })}
                  @click=${() =>
                    window.open(`${getBuchungsheldRootUrlByDeployment()}profile?preselectedTab=profile`, '_self')}
                >
                  ${m.candidate_profile_nav_profile()}
                </x-flex>

                <x-flex
                  padding="sm"
                  ${css({
                    cursor: 'pointer',
                    borderLeft: `4px solid ${design?.colors.accent}`,
                    backgroundColor: design?.colors.neutral.shade(50),
                    paddingLeft: design?.spacing.sm,
                    color: design?.colors.accent,
                    fontWeight: 'bold',
                  })}
                >
                  ${m.candidate_profile_nav_matching_week()}
                </x-flex>
              </x-flex>

              <div class="p-2 w-full max-w-6xl flex flex-col gap-4">
                <tr-flex
                  vertical
                  space="md"
                  ${css({
                    width: '100%',
                    padding: '24px',
                    borderRadius: '8px',
                    color: 'white',
                    position: 'relative',
                    background:
                      'linear-gradient(-135deg, var(--color-info, royalblue) 0%, var(--color-primary, dodgerblue) 100%)',
                  })}
                >
                  <tr-icon
                    ${css({
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      fontSize: '1.75rem',
                      cursor: 'pointer',
                      opacity: '0.9',
                      color: 'white',
                      // '&:hover': { opacity: '1' },
                    })}
                    @click=${() =>
                      XDialog.showPopup({
                        content: () =>
                          html` <tr-flex ${css({ padding: '40px', maxWidth: '600px' })}>
                            <h2>${m.candidate_profile_calendar_dialog_title()}</h2>
                            ${getExplanations(globalLanguageSubject.value).map((explanation, index) => {
                              return html`
                                <tr-toggle-content .open=${index === 0} .label=${explanation.title}>
                                  <ol>
                                    ${explanation.steps.map(step => html` <li>${step.text}</li> `)}
                                  </ol>
                                </tr-toggle-content>
                              `;
                            })}
                          </tr-flex>`,
                      })}
                    >info</tr-icon
                  >
                  <!-- Header with icon and title -->
                  <tr-flex horizontal space="xs" crossaxis-center>
                    <tr-icon ${css({ fontSize: '1.5rem' })}>calendar_month</tr-icon>
                    <tr-h3 weight="bold">${m.candidate_profile_calendar_sync_title()}</tr-h3>
                  </tr-flex>
                  <!-- Subtitle -->
                  <tr-text
                    >${m.candidate_profile_calendar_sync_subtitle_p1()}
                    ${m.candidate_profile_calendar_sync_subtitle_p2()}</tr-text
                  >

                  <!-- Link input -->
                  <tr-flex horizontal crossaxis-center ${css({ width: '100%' })}>
                    <tr-input
                      flex
                      ${css({ cursor: 'pointer', width: '100%', color: 'black' })}
                      disabled
                      .value=${calendarLink}
                      icon="content_copy"
                      @click=${() => {
                        void navigator.clipboard.writeText(calendarLink);
                        return XSnackbar.show(m.copy_link_toast());
                      }}
                    ></tr-input>
                  </tr-flex>
                </tr-flex>

                <div class="flex items-center justify-between mt-4">
                  <h1 class="text-3xl font-bold">${m.candidate_profile_title_application_overview()}</h1>
                  <a class="btn btn-outline btn-primary" href="/embed">
                    <span class="icon-[material-symbols--add] mr-2"></span>
                    ${m.candidate_profile_submit_more_applications()}
                  </a>
                </div>

                ${CandidateJobPostingOverview()}
              </div>
            </x-flex>
          </div>

          <!-- Mobile Content (No Sidebar) -->
          <div class="block lg:hidden">
            <div class="p-2 w-full max-w-6xl flex flex-col gap-4">
              <tr-flex
                vertical
                space="md"
                ${css({
                  width: '100%',
                  padding: '24px',
                  borderRadius: '8px',
                  color: 'white',
                  position: 'relative',
                  background:
                    'linear-gradient(-135deg, var(--color-info, royalblue) 0%, var(--color-primary, dodgerblue) 100%)',
                })}
              >
                <tr-icon
                  ${css({
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    fontSize: '1.75rem',
                    cursor: 'pointer',
                    opacity: '0.9',
                    color: 'white',
                  })}
                  @click=${() =>
                    XDialog.showPopup({
                      content: () =>
                        html` <tr-flex ${css({ padding: '40px', maxWidth: '600px' })}>
                          <h2>${m.candidate_profile_calendar_dialog_title()}</h2>
                          ${getExplanations(globalLanguageSubject.value).map((explanation, index) => {
                            return html`
                              <tr-toggle-content .open=${index === 0} .label=${explanation.title}>
                                <ol>
                                  ${explanation.steps.map(step => html` <li>${step.text}</li> `)}
                                </ol>
                              </tr-toggle-content>
                            `;
                          })}
                        </tr-flex>`,
                    })}
                  >info</tr-icon
                >
                <!-- Header with icon and title -->
                <tr-flex horizontal space="xs" crossaxis-center>
                  <tr-icon ${css({ fontSize: '1.5rem' })}>calendar_month</tr-icon>
                  <tr-h3 weight="bold">${m.candidate_profile_calendar_sync_title()}</tr-h3>
                </tr-flex>
                <!-- Subtitle -->
                <tr-text
                  >${m.candidate_profile_calendar_sync_subtitle_p1()}
                  ${m.candidate_profile_calendar_sync_subtitle_p2()}</tr-text
                >

                <!-- Link input -->
                <tr-flex horizontal crossaxis-center ${css({ width: '100%' })}>
                  <tr-input
                    flex
                    ${css({ cursor: 'pointer', width: '100%', color: 'black' })}
                    disabled
                    .value=${calendarLink}
                    icon="content_copy"
                    @click=${() => {
                      void navigator.clipboard.writeText(calendarLink);
                      return XSnackbar.show(m.copy_link_toast());
                    }}
                  ></tr-input>
                </tr-flex>
              </tr-flex>

              <div class="flex flex-col items-center justify-between mt-4 gap-4">
                <h1 class="text-3xl font-bold">${m.candidate_profile_title_application_overview()}</h1>
                <a class="btn btn-outline btn-primary" href="/embed">
                  <span class="icon-[material-symbols--add] mr-2"></span>
                  ${m.candidate_profile_submit_more_applications()}
                </a>
              </div>

              ${CandidateJobPostingOverview()}
            </div>
          </div>
        </x-section>
      </x-flex>
    </x-flex>
  `;
});
